import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import styles from "./BlogDetail.module.css";

function BlogDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_STRAPI_API_URL;
        const baseUrl = process.env.REACT_APP_STRAPI_BASE_URL;

        const response = await fetch(
          `${apiUrl}/articles?filters[slug][$eq]=${slug}&populate=*`,
          {
            headers: {
              Accept: "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch article");
        }

        const result = await response.json();
        const articleData = result.data?.[0];

        if (!articleData) {
          throw new Error("Article not found");
        }

        // Now fetch the full article using documentId for consistency
        const detailResponse = await fetch(
          `${apiUrl}/articles/${articleData.documentId}?populate=*`,
          {
            headers: {
              Accept: "application/json",
            },
          },
        );

        if (!detailResponse.ok) {
          throw new Error("Failed to fetch article details");
        }

        const detailResult = await detailResponse.json();
        setArticle({
          ...detailResult.data,
          coverUrl: detailResult.data.cover?.url
            ? `${baseUrl}${detailResult.data.cover.url}`
            : null,
        });
        setError(null);
      } catch (err) {
        console.error("Error fetching article:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const renderBlock = (block) => {
    switch (block.__component) {
      case "shared.rich-text":
        return (
          <div key={block.id} className={styles.richText}>
            <ReactMarkdown>{block.body}</ReactMarkdown>
          </div>
        );
      case "shared.quote":
        return (
          <blockquote key={block.id} className={styles.quote}>
            <p className={styles.quoteBody}>{block.body}</p>
            {block.title && (
              <cite className={styles.quoteAuthor}>— {block.title}</cite>
            )}
          </blockquote>
        );
      case "shared.media":
        return (
          <div key={block.id} className={styles.mediaBlock}>
            <p className={styles.mediaPlaceholder}>Media Block #{block.id}</p>
          </div>
        );
      case "shared.slider":
        return (
          <div key={block.id} className={styles.sliderBlock}>
            <p className={styles.sliderPlaceholder}>Slider Block #{block.id}</p>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading article...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Error loading article: {error}</div>
        <Link to="/blogs" className={styles.backLink}>
          ← Back to Blogs
        </Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Article not found</div>
        <Link to="/blogs" className={styles.backLink}>
          ← Back to Blogs
        </Link>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <title>{`POD: ${article.title}`}</title>
        <meta
          name="description"
          content={article.description || article.title}
        />
        <meta property="og:title" content={article.title} />
        <meta
          property="og:description"
          content={article.description || article.title}
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        {article.coverUrl && (
          <meta property="og:image" content={article.coverUrl} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta
          name="twitter:description"
          content={article.description || article.title}
        />
        {article.coverUrl && (
          <meta name="twitter:image" content={article.coverUrl} />
        )}
      </Helmet>

      <Link to="/blogs" className={styles.backLink}>
        ← Back to Blogs
      </Link>

      <article className={styles.article}>
        {article.coverUrl && (
          <div className={styles.coverImage}>
            <img src={article.coverUrl} alt={article.title} />
          </div>
        )}

        <div className={styles.articleHeader}>
          <div className={styles.metadata}>
            {article.category && (
              <span className={styles.category}>{article.category.name}</span>
            )}
            <span className={styles.date}>
              {formatDate(article.publishedAt)}
            </span>
          </div>

          <h1 className={styles.title}>{article.title}</h1>

          {article.description && (
            <p className={styles.description}>{article.description}</p>
          )}

          {article.author && (
            <div className={styles.authorInfo}>
              <div className={styles.authorDetails}>
                <span className={styles.authorName}>{article.author.name}</span>
                <span className={styles.authorEmail}>
                  {article.author.email}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className={styles.content}>
          {article.blocks && article.blocks.length > 0 ? (
            article.blocks.map((block) => renderBlock(block))
          ) : (
            <p>No content available</p>
          )}
        </div>

        <div className={styles.articleFooter}>
          <div className={styles.timestamps}>
            <p>
              <strong>Published:</strong> {formatDate(article.publishedAt)}
            </p>
            <p>
              <strong>Last Updated:</strong> {formatDate(article.updatedAt)}
            </p>
          </div>
        </div>
      </article>
    </div>
  );
}

export default BlogDetail;
